import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconButton, Menu } from '../ui';
import { BookmarkIcon, MoreIcon } from '../ui/icons';
import { useViewActions } from '../components/collection/SavedViewActions';
import { activeView, useSavedViews, viewLink } from './savedViews';

/**
 * The caller's saved views of the open collection, under its entry in the
 * sidebar (phase 3 §5.1): each a link to its list (with its view mode), the
 * one shown highlighted, a "…" menu to rename, move or delete it. No counts:
 * they would cost a list request per view.
 */
function SavedViewsNav({ inventory, onNavigate }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { views } = useSavedViews(inventory.id);
  const { items, dialog } = useViewActions(inventory.id);
  if (!views || views.length === 0) return dialog;
  const onList = location.pathname.replace(/\/+$/, '') === `/c/${inventory.id}`;
  const active = onList ? activeView(views, location, inventory) : { view: null, modified: false };

  return (
    <>
      <ul className="shell-views" aria-label={t('views.listLabel', { name: inventory.name })}>
        {views.map((view) => {
          const isActive = active.view && active.view.id === view.id;
          const link = viewLink(inventory.id, view);
          return (
            <li key={view.id} className={`shell-views__item${isActive ? ' is-active' : ''}`}>
              <Link to={link.to} state={link.state} className="shell-views__link" onClick={onNavigate}
                    aria-current={isActive ? 'page' : undefined}>
                <BookmarkIcon size={16} className="shell-views__icon" />
                <span className="shell-views__name">{view.name}</span>
                {isActive && active.modified && (
                  <span className="shell-views__modified">
                    <span aria-hidden="true">•</span>
                    <span className="visually-hidden">{t('views.modified')}</span>
                  </span>
                )}
              </Link>
              <Menu
                label={t('views.actionsFor', { name: view.name })}
                align="end"
                className="shell-views__menu"
                items={items(view)}
                trigger={(props) => (
                  <IconButton {...props} icon={MoreIcon} size="sm" variant="ghost" label={t('views.actionsFor', { name: view.name })} />
                )}
              />
            </li>
          );
        })}
      </ul>
      {dialog}
    </>
  );
}

export default SavedViewsNav;
